// components/cards/message/ArtifactButton.tsx
import { memo } from "react";
import { Button } from "@/components/ui/button";
import { ChartNoAxesCombined, Code, Table, Database } from "lucide-react";
import { cn } from "@/lib/utils";
import ArtifactLoadingSpinner from "./ArtifactLoadingSpinner";

interface ArtifactButtonProps {
  id: string;
  type: string;
  title: string;
  isLoading: boolean;
  onClick: () => void;
}

const getArtifactIcon = (type: string) => {
  switch (type) {
    case "graph":
      return <ChartNoAxesCombined className="h-6 w-6" />;
    case "code":
      return <Code className="h-6 w-6" />;
    case "table":
      return <Table className="h-6 w-6" />;
    case "sql":
      return <Database className="h-6 w-6" />;
    default:
      return <ChartNoAxesCombined className="h-6 w-6" />;
  }
};

export const ArtifactButton = memo(({ 
  type, 
  title, 
  isLoading, 
  onClick 
}: ArtifactButtonProps) => {
  return (
    <Button
      variant="outline"
      className={cn(
        "flex min-h-14 h-fit w-full justify-start text-start my-2 transition-all",
        isLoading && "border-primary/50 bg-primary/5 dark:bg-primary/10 artifact-loading-button"
      )}
      onClick={onClick}
    >
      <div className="flex h-full w-full items-center justify-between gap-4">
        <div className="flex flex-col justify-center gap-1 text-wrap">
          <div className="text-base font-medium">{title}</div>
          <div className="text-xs text-muted-foreground capitalize">{type}</div>
        </div>
        {isLoading ? <ArtifactLoadingSpinner /> : getArtifactIcon(type)}
      </div>
    </Button>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.id === nextProps.id &&
    prevProps.type === nextProps.type &&
    prevProps.title === nextProps.title &&
    prevProps.isLoading === nextProps.isLoading
  );
});